import { useState } from 'react'
import Form, { type IChangeEvent } from '@rjsf/core'
import validator from '@rjsf/validator-ajv8'
import {
  payloadSchema,
  type PayloadSchemaFormData
} from '../create-schema-modal/CreateSchemaModal'
import type { PayloadSchema } from '../../types/types'
import { useCreatePayloadSchemaMutation } from '../../hooks/usePayloadSchema'
import { templates, widgets } from '../ui-schema-components'
import { ModalDialog, Mtds, useErrorToast } from 'prove-common'
import { useNavigate } from 'react-router'

type CopySchemaModalProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  schema: PayloadSchema
  contractId: string | undefined
}

export const CopySchemaModal = ({
  isOpen,
  setOpen,
  schema,
  contractId
}: CopySchemaModalProps) => {
  const navigate = useNavigate()
  const createPayloadSchema = useCreatePayloadSchemaMutation()
  const showError = useErrorToast('kopiere skjemaet')
  const [formData, setFormData] = useState<PayloadSchemaFormData>({
    name: `${schema.name} (kopi)`
  })

  const onSubmit = async ({
    formData
  }: IChangeEvent<PayloadSchemaFormData>): Promise<void> => {
    if (formData != null) {
      try {
        const newSchema = await createPayloadSchema.mutateAsync({
          name: formData.name,
          schema: schema.schema,
          labId: schema.labId ?? undefined
        })
        setOpen(false)
        Mtds.toast.success('Skjemaet er kopiert')
        const url = contractId
          ? `/contracts/${contractId}/schemas/${newSchema.id}`
          : `/schemas/${newSchema.id}`
        navigate(url)
      } catch (error) {
        showError(error)
      }
    }
  }

  return (
    <ModalDialog
      id='copy-schema-modal'
      closedby='any'
      open={isOpen}
      onClose={() => {
        setOpen(false)
        setFormData({ name: `${schema.name} (kopi)` })
      }}
    >
      <Mtds.Button
        command='close'
        commandfor='copy-schema-modal'
        aria-label='Lukk'
      />
      <Mtds.Prose>
        <Mtds.Heading>Kopier skjema</Mtds.Heading>
        <p>
          Lager en kopi av skjemaet med alle feltene. Du kan gi kopien et nytt
          navn før den opprettes.
        </p>
        <Form<PayloadSchemaFormData>
          noHtml5Validate
          formData={formData}
          schema={payloadSchema}
          widgets={widgets}
          templates={templates}
          showErrorList={false}
          validator={validator}
          onChange={(data) => setFormData(data.formData ?? formData)}
          onSubmit={onSubmit}
        >
          <Mtds.Flex className='mt-8'>
            <Mtds.Button command='close' commandfor='copy-schema-modal'>
              Lukk
            </Mtds.Button>
            <Mtds.Button
              data-variant='primary'
              type='submit'
              aria-busy={createPayloadSchema.isPending}
              disabled={createPayloadSchema.isPending}
            >
              Kopier
            </Mtds.Button>
          </Mtds.Flex>
        </Form>
      </Mtds.Prose>
    </ModalDialog>
  )
}
